/**
 * ─── HRIS Entity Definitions ─────────────────────────────────
 * Self-registers all HRIS entities with the Entity Registry.
 *
 * Entities:
 *  - hris_employees       — Employee master records
 *  - hris_attendance      — Time logs & biometric entries
 *  - hris_shifts          — Work shift definitions
 *  - hris_devices         — Biometric device configurations
 *  - hris_payroll_periods — Payroll processing periods
 *  - hris_payslips        — Generated payslips
 *  - hris_form_2316       — BIR annual tax certificates
 */

import { EntityRegistry } from '@repo/core'
import type { EntityDefinition } from '@repo/core'
import type {
  Employee, AttendanceRecord, Shift, BiometricDevice,
  PayrollPeriod, Payslip, Form2316,
} from './hris.schema'
import { HrisHooks } from './hris.hooks'

// ─── Employee ──────────────────────────────────────────────

export const EmployeeEntity: EntityDefinition<Employee> = {
  name: 'hris_employees',
  ui: {
    label: 'Employee',
    labelPlural: 'Employees',
    icon: 'Users',
    routePath: 'hris/employees',
    color: 'blue',
    showInNav: true,
    navOrder: 10,
    navGroup: 'HRIS',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'normal' },
  audit: { enabled: true, excludeFields: ['version'] },
  rbac: { enabled: true, permissionPrefix: 'hris_employee' },
  hooks: HrisHooks,
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: true, field: 'deletedAt' },
}

// ─── Attendance ────────────────────────────────────────────

export const AttendanceEntity: EntityDefinition<AttendanceRecord> = {
  name: 'hris_attendance',
  ui: {
    label: 'Attendance',
    labelPlural: 'Attendance Records',
    icon: 'Clock',
    routePath: 'hris/attendance',
    color: 'green',
    showInNav: true,
    navOrder: 20,
    navGroup: 'HRIS',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'critical' },
  audit: { enabled: true },
  rbac: { enabled: true, permissionPrefix: 'hris_attendance' },
  hooks: {},
  pagination: 'cursor',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: true, field: 'deletedAt' },
}

// ─── Shifts ────────────────────────────────────────────────

export const ShiftEntity: EntityDefinition<Shift> = {
  name: 'hris_shifts',
  ui: {
    label: 'Shift',
    labelPlural: 'Shifts',
    icon: 'Calendar',
    routePath: 'hris/shifts',
    color: 'purple',
    showInNav: true,
    navOrder: 15,
    navGroup: 'HRIS',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'normal' },
  audit: { enabled: true },
  rbac: { enabled: true, permissionPrefix: 'hris_shift' },
  hooks: {},
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: true, field: 'deletedAt' },
}

// ─── Biometric Devices ─────────────────────────────────────

export const BiometricDeviceEntity: EntityDefinition<BiometricDevice> = {
  name: 'hris_devices',
  ui: {
    label: 'Biometric Device',
    labelPlural: 'Biometric Devices',
    icon: 'Fingerprint',
    routePath: 'hris/devices',
    color: 'red',
    showInNav: true,
    navOrder: 25,
    navGroup: 'HRIS',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'background' },
  audit: { enabled: true },
  rbac: { enabled: true, permissionPrefix: 'hris_device' },
  hooks: {},
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: true, field: 'deletedAt' },
}

// ─── Payroll Periods ───────────────────────────────────────

export const PayrollPeriodEntity: EntityDefinition<PayrollPeriod> = {
  name: 'hris_payroll_periods',
  ui: {
    label: 'Payroll Period',
    labelPlural: 'Payroll Periods',
    icon: 'Briefcase',
    routePath: 'hris/payroll',
    color: 'blue',
    showInNav: true,
    navOrder: 30,
    navGroup: 'HRIS',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'critical' },
  audit: { enabled: true, excludeFields: ['version'] },
  rbac: { enabled: true, permissionPrefix: 'hris_payroll' },
  hooks: {},
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: true, field: 'deletedAt' },
}

// ─── Payslips ──────────────────────────────────────────────

export const PayslipEntity: EntityDefinition<Payslip> = {
  name: 'hris_payslips',
  ui: {
    label: 'Payslip',
    labelPlural: 'Payslips',
    icon: 'Receipt',
    routePath: 'hris/payslips',
    color: 'green',
    showInNav: true,
    navOrder: 35,
    navGroup: 'HRIS',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'normal' },
  audit: { enabled: true, excludeFields: ['version'] },
  rbac: { enabled: true, permissionPrefix: 'hris_payslip' },
  hooks: {},
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: true, field: 'deletedAt' },
}

// ─── Form 2316 ─────────────────────────────────────────────

export const Form2316Entity: EntityDefinition<Form2316> = {
  name: 'hris_form_2316',
  ui: {
    label: 'Form 2316',
    labelPlural: 'BIR Form 2316',
    icon: 'FileText',
    routePath: 'hris/form-2316',
    color: 'yellow',
    showInNav: true,
    navOrder: 40,
    navGroup: 'HRIS',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'background' },
  audit: { enabled: true, excludeFields: ['version'] },
  rbac: { enabled: true, permissionPrefix: 'hris_form2316' },
  hooks: {},
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: true, field: 'deletedAt' },
}

// ─── Self-Register All ──────────────────────────────────────

EntityRegistry.register(EmployeeEntity)
EntityRegistry.register(AttendanceEntity)
EntityRegistry.register(ShiftEntity)
EntityRegistry.register(BiometricDeviceEntity)
EntityRegistry.register(PayrollPeriodEntity)
EntityRegistry.register(PayslipEntity)
EntityRegistry.register(Form2316Entity)
